"use client";
import * as React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "./Icons";

/* ──────── Badge ──────── */
type BadgeTone = "neutral" | "primary" | "success" | "warn" | "danger" | "dark";

const BADGE_TONES: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: "var(--surface-2)", fg: "var(--ink-2)" },
  primary: { bg: "var(--primary-50)", fg: "var(--primary-700)" },
  success: { bg: "#e7f7ee", fg: "#08793e" },
  warn: { bg: "#fef3c7", fg: "#92400e" },
  danger: { bg: "#fee2e2", fg: "#b91c1c" },
  dark: { bg: "#111827", fg: "#fff" },
};

export function Badge({
  children,
  tone = "neutral",
  style,
}: {
  children: React.ReactNode;
  tone?: BadgeTone;
  style?: React.CSSProperties;
}) {
  const t = BADGE_TONES[tone] ?? BADGE_TONES.neutral;
  return (
    <span
      className="badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 8px",
        borderRadius: "var(--radius-chip)",
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.fg,
        lineHeight: 1,
        letterSpacing: 0.1,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/* ──────── Button ──────── */
type ButtonProps = {
  children: React.ReactNode;
  variant?: "primary" | "dark" | "soft" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  full?: boolean;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  style?: React.CSSProperties;
  disabled?: boolean;
  type?: "button" | "submit";
  /** Stable hook for automated tests (pos_v2 table-guests harness). */
  testId?: string;
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  full,
  leading,
  trailing,
  onClick,
  style,
  disabled,
  type = "button",
  testId,
}: ButtonProps) {
  const sizes = {
    sm: { h: 36, px: 14, fs: 13 },
    md: { h: 44, px: 18, fs: 14 },
    lg: { h: 52, px: 22, fs: 15 },
  } as const;
  const variants = {
    primary: { bg: "var(--primary-600)", fg: "#fff", bd: "transparent", sh: "var(--shadow-cta)" },
    dark: { bg: "var(--cart-bg)", fg: "var(--cart-fg)", bd: "transparent", sh: "var(--shadow-cta)" },
    soft: { bg: "var(--primary-50)", fg: "var(--primary-700)", bd: "transparent", sh: "none" },
    outline: { bg: "transparent", fg: "var(--ink-1)", bd: "var(--border)", sh: "none" },
    ghost: { bg: "transparent", fg: "var(--ink-1)", bd: "transparent", sh: "none" },
  } as const;
  const s = sizes[size];
  const v = variants[variant];
  return (
    <button
      type={type}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height: s.h,
        padding: `0 ${s.px}px`,
        fontSize: s.fs,
        fontWeight: 600,
        borderRadius: "var(--radius-btn)",
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`,
        boxShadow: v.sh,
        width: full ? "100%" : "auto",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "transform 120ms ease, opacity 150ms",
        fontFamily: "inherit",
        letterSpacing: -0.05,
        ...style,
      }}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
}

/* ──────── Card ──────── */
export function Card({
  children,
  style,
  padding = 0,
  onClick,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  padding?: number;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--card-bg)",
        border: "var(--card-border)",
        boxShadow: "var(--card-shadow)",
        borderRadius: "var(--radius-card)",
        padding,
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ──────── TopBar ──────── */
export function TopBar({
  title,
  onBack,
  right,
  transparent,
  sticky = true,
}: {
  title: React.ReactNode;
  onBack?: () => void;
  right?: React.ReactNode;
  transparent?: boolean;
  sticky?: boolean;
}) {
  return (
    <div
      style={{
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 16px 10px",
        background: transparent ? "transparent" : "var(--surface-1)",
        backdropFilter: transparent ? "blur(10px)" : "none",
        WebkitBackdropFilter: transparent ? "blur(10px)" : "none",
        borderBottom: transparent ? "none" : "1px solid var(--border)",
        color: "var(--ink-1)",
      }}
    >
      {onBack && (
        <button
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 38,
            height: 38,
            display: "grid",
            placeItems: "center",
            borderRadius: 999,
            background: transparent ? "rgba(255,255,255,0.85)" : "var(--surface-2)",
            border: "1px solid var(--border)",
            color: "var(--ink-1)",
            cursor: "pointer",
          }}
        >
          <Icons.back size={18} />
        </button>
      )}
      <div
        className="display"
        style={{
          flex: 1,
          fontSize: 17,
          color: "inherit",
        }}
      >
        {title}
      </div>
      {right}
    </div>
  );
}

/* ──────── SectionHeader ──────── */
export function SectionHeader({
  title,
  action,
  sub,
  eyebrow,
}: {
  title: React.ReactNode;
  action?: React.ReactNode;
  sub?: React.ReactNode;
  eyebrow?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginBottom: 12,
      }}
    >
      <div>
        {eyebrow && (
          <div
            style={{
              textTransform: "uppercase",
              letterSpacing: 0.08,
              fontSize: 11,
              fontWeight: 600,
              color: "var(--ink-2)",
              marginBottom: 4,
              display: "none",
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          className="section-title display"
          style={{
            fontSize: 18,
            color: "var(--ink-1)",
          }}
        >
          {title}
        </div>
        {sub && <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

/* ──────── Stepper ──────── */
export function Stepper({
  value,
  onChange,
  min = 1,
  max = 99,
  size = "md",
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  size?: "sm" | "md";
}) {
  const h = size === "sm" ? 32 : 38;
  const btn: React.CSSProperties = {
    width: h,
    height: h,
    display: "grid",
    placeItems: "center",
    borderRadius: 999,
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    color: "var(--ink-1)",
    cursor: "pointer",
  };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <button style={btn} onClick={() => onChange(Math.max(min, value - 1))} aria-label="Decrease">
        <Icons.minus size={16} />
      </button>
      <div className="num" style={{ minWidth: 22, textAlign: "center", fontWeight: 700, fontSize: 15 }}>
        {value}
      </div>
      <button style={btn} onClick={() => onChange(Math.min(max, value + 1))} aria-label="Increase">
        <Icons.plus size={16} />
      </button>
    </div>
  );
}

/* ──────── BottomNav ──────── */
export function BottomNav({
  active,
  onChange,
  cartCount = 0,
}: {
  active: string;
  onChange: (id: string) => void;
  cartCount?: number;
}) {
  const { t } = useTranslation();
  const items = [
    { id: "home", label: t("v2.nav.menu"), icon: Icons.home },
    { id: "history", label: t("v2.nav.orders"), icon: Icons.receipt },
    { id: "cart", label: t("v2.nav.cart"), icon: Icons.bag, badge: cartCount },
    { id: "profile", label: t("v2.nav.profile"), icon: Icons.user },
  ];
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6px)",
        background: "var(--surface-1)",
        borderTop: "1px solid var(--border)",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        zIndex: 18,
      }}
    >
      {items.map((it) => {
        const on = active === it.id;
        const Ic = it.icon;
        return (
          <button
            key={it.id}
            onClick={() => onChange(it.id)}
            style={{
              background: "transparent",
              border: 0,
              padding: "10px 0 6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              color: on ? "var(--primary-700)" : "var(--ink-3)",
              cursor: "pointer",
              position: "relative",
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                position: "relative",
                width: 28,
                height: 24,
                display: "grid",
                placeItems: "center",
              }}
            >
              <Ic size={22} />
              {it.badge && it.badge > 0 ? (
                <span
                  style={{
                    position: "absolute",
                    top: -7,
                    insetInlineEnd: -7,
                    minWidth: 17,
                    height: 17,
                    padding: "0 5px",
                    borderRadius: 999,
                    background: "var(--primary-600)",
                    color: "#fff",
                    border: "2px solid var(--surface-1)",
                    fontSize: 10,
                    fontWeight: 700,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {it.badge}
                </span>
              ) : null}
            </div>
            <span style={{ fontSize: 11, fontWeight: 600 }}>{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────── StickyBar ──────── */
export function StickyBar({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const [host, setHost] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setHost(document.querySelector<HTMLElement>(".phone-shell"));
  }, []);

  const bar = (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)",
        padding: "12px 16px",
        background: "var(--surface-1)",
        borderTop: "1px solid var(--border)",
        zIndex: 17,
        ...style,
      }}
    >
      {children}
    </div>
  );

  return host ? createPortal(bar, host) : bar;
}

/* ──────── Radio (filled dot) ──────── */
export function Radio({ on }: { on: boolean }) {
  return (
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        border: `2px solid ${on ? "var(--primary-600)" : "var(--border-strong)"}`,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {on && (
        <span
          style={{ width: 10, height: 10, borderRadius: 999, background: "var(--primary-600)" }}
        />
      )}
    </div>
  );
}

/* ──────── Toggle ──────── */
export function Toggle({
  on,
  onChange,
  disabled = false,
}: {
  on: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        padding: 2,
        border: 0,
        cursor: disabled ? "default" : "pointer",
        background: on ? "var(--primary-600)" : "var(--surface-2-strong)",
        transition: "background 200ms",
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transform: `translateX(${on ? 18 : 0}px)`,
          transition: "transform 200ms",
        }}
      />
    </button>
  );
}

/* ──────── Totals row ──────── */
export function Row({
  label,
  value,
  bold,
  accent,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span
        style={{
          fontSize: bold ? 15 : 13.5,
          color: bold ? "var(--ink-1)" : "var(--ink-2)",
          fontWeight: bold ? 700 : 500,
        }}
      >
        {label}
      </span>
      <span
        className="num"
        style={{
          fontSize: bold ? 17 : 13.5,
          fontWeight: bold ? 700 : 600,
          color: accent ? "var(--primary-700)" : "var(--ink-1)",
        }}
      >
        {value}
      </span>
    </div>
  );
}
